// ORG-CHART-001 Sprint 0153 — API client for the designation-source
// org chart endpoints. Distinct from `roleMasterApi.orgChart()` (which
// reads RoleResponsibilityMaster); the new endpoints read directly
// from DesignationMaster and honour the post-0153 hide_from_org_chart
// / legacy_role_flag / function_group / alias_of_designation_code
// metadata.
import apiClient from '@/shared/api/client'

const BASE = '/hr/org'

export type FunctionGroup =
  | 'BOARD'
  | 'FINANCE'
  | 'HR'
  | 'OPERATIONS'
  | 'STORES'
  | 'QUALITY'
  | 'TECHNICAL'
  | 'PROCUREMENT'
  | 'SALES'
  | 'IT'
  | 'SECURITY'
  | 'ADMIN'

export type Criticality = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'

export interface DesignationNode {
  id: string
  designation_code: string
  designation_name: string
  category: string | null
  role_purpose: string | null
  role_level: number | null
  department_code: string | null
  reports_to_designation_code: string | null
  default_shift_type: string | null
  criticality: Criticality | null
  backup_designation_code: string | null
  statutory_role_flag: boolean
  statutory_reference: string | null
  min_manpower_estimate: number | null
  max_manpower_estimate: number | null
  role_grade: string | null
  education_required: string | null
  experience_min_years: number | null
  remarks: string | null
  is_active: boolean
  // Sprint 0153
  display_order: number | null
  function_group: FunctionGroup | null
  hide_from_org_chart: boolean
  legacy_role_flag: boolean
  alias_of_designation_code: string | null
  operational_role_flag: boolean
  approval_role_flag: boolean
  // recursion
  children: DesignationNode[]
}

export interface OrphanEntry {
  designation_code: string
  designation_name: string
  reports_to: string | null
  function_group: FunctionGroup | null
  suggested_action: string
}

export interface OrphanAudit {
  missing_parent: OrphanEntry[]
  unparented: OrphanEntry[]
  self_parent: OrphanEntry[]
  total_orphans: number
  total_active_designations: number
}

export interface HierarchyValidation {
  cycles: string[][]
  max_depth: number
  multi_root: {
    designation_code: string
    designation_name: string
    function_group: FunctionGroup | null
  }[]
  total_nodes: number
  visible_nodes: number
}

export interface AssignPersonLogin {
  created: boolean
  username: string | null
  temp_password: string | null
  was_existing: boolean
}

export interface AssignPersonResult {
  assigned: boolean
  employee_id: string
  designation_code: string
  designation_name: string
  needs_login: boolean
  login: AssignPersonLogin | null
}

export const hrOrgApi = {
  /**
   * Assign an active employee to a designation (org-chart node) and,
   * when create_login is true and the designation resolves a default
   * role, auto-provision an ERP login. Returns a one-time temp_password
   * inside `login` when a fresh login was created.
   */
  assignPerson: async (payload: {
    employee_id: string
    designation_code: string
    create_login: boolean
  }): Promise<AssignPersonResult> =>
    (await apiClient.post<AssignPersonResult>(`${BASE}/assign-person`, payload))
      .data,

  /**
   * Hierarchical designation tree (top-down).
   * - `hide_pending` (default false): omit rows whose
   *   RoleResponsibilityMaster.approval_status='DETAILS_PENDING'.
   * - `hide_legacy` (default true): omit hide_from_org_chart=true,
   *   legacy_role_flag=true, AND alias-of-canonical rows. Default ON
   *   gives the clean canonical tree.
   */
  orgChart: async (
    hide_pending = false,
    hide_legacy = true,
  ): Promise<DesignationNode[]> => {
    const params: Record<string, unknown> = { hide_pending, hide_legacy }
    return (
      await apiClient.get<DesignationNode[]>(`${BASE}/org-chart`, { params })
    ).data
  },

  orphans: async (): Promise<OrphanAudit> =>
    (await apiClient.get<OrphanAudit>(`${BASE}/orphans`)).data,

  validate: async (): Promise<HierarchyValidation> =>
    (await apiClient.get<HierarchyValidation>(`${BASE}/validate`)).data,
}
